import React, { useEffect, useRef, useState } from "react";
import IngestHome from "./views/IngestHome.jsx";
import HandheldHome from "./views/handheld/HandheldHome.jsx";
import RigV2App from "./views/rigv2/RigV2App.jsx";
import { isEventUser } from "../../lib/eventAccount.js";
import { usePageIds } from "../../lib/feedback/pageIds.js";
import { takePendingJob } from "../../lib/feedback/deepLink.js";

/**
 * Top-level ingest app, mounted from src/App.jsx when activeApp === "ingest".
 *
 * The front door is the locked mockup's two-card fork (IngestHome):
 *   Rigs               → RigV2App (the rig redesign: Settings · Rig · Roster · Captures)
 *   Handheld equipment → HandheldHome (the Equipment ingest line)
 *
 * The old v1 CaptureArraysView ("Waiting for a QR scan") is retired from the UI
 * as of 2026-06-10 (Ivin's call) — the file stays in views/ unrouted; the capture
 * engine it shared (useCaptureState + ingestCallables) lives on under RigV2App.
 *
 * An event account (docs/ops/event-role.md) gets Rigs only: the front door
 * shows the one card, and Handheld (which lists every job) never mounts.
 *
 * A feedback inbox link (?go=ingest&job=<id>, src/lib/feedback/deepLink.js)
 * opens the Rigs console on that job.
 */
export default function IngestApp({ navClickTick, user, onSignOut }) {
  const [linkedJob, setLinkedJob] = useState(() => takePendingJob("ingest"));
  const [door, setDoorState] = useState(linkedJob ? "rigs" : "home"); // home | rigs | handheld
  // Leaving the linked job's console forgets the link: the next Rigs opens the picker as usual.
  const setDoor = (d) => { setLinkedJob(null); setDoorState(d); };
  usePageIds("ingest", { door });

  // Any topbar nav click returns Ingest to its front door (same pattern as
  // Jobs). Not on mount (the tick it mounted with), so a linked job stays open.
  const mountTick = useRef(navClickTick);
  useEffect(() => {
    if (navClickTick === mountTick.current) return;
    mountTick.current = navClickTick;
    setLinkedJob(null);
    setDoorState("home");
  }, [navClickTick]);

  const rigsOnly = isEventUser(user);
  if (door === "rigs") return <RigV2App user={user} initialJobId={linkedJob} onExit={() => setDoor("home")} onSignOut={onSignOut} />;
  if (door === "handheld" && !rigsOnly) return <HandheldHome user={user} onExit={() => setDoor("home")} />;
  return <IngestHome onPick={setDoor} rigsOnly={rigsOnly} />;
}
